import { useId } from 'react';
import type {
  InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';
import { InfoTip } from './InfoTip';

/**
 * Form controls, as primitives.
 *
 * Before this file the product had eighty-six hand-rolled `<input>` and
 * `<select>` elements, each with its own border, height and padding written
 * out inline. That is how a design system dies: not in one decision, but in
 * eighty-six small ones nobody was making on purpose.
 *
 * The accessibility here is also not incidental. `Field` generates the id and
 * wires the label, the description and the error message to the control with
 * `htmlFor` and `aria-describedby`, so a screen reader reads the error with
 * the field rather than leaving it stranded elsewhere on the page. Doing that
 * by hand eighty-six times is a thing nobody finishes.
 */

const CONTROL = cn(
  'w-full rounded-input border bg-white px-3 text-body text-neutral-900',
  'placeholder:text-neutral-500',
  'transition-colors duration-hover',
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0',
  'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500',
);

export function Field({
  label, hint, info, status, error, required, children, className,
}: {
  label: string;
  /**
   * Guidance about the field, behind ⓘ beside the label — never a caption
   * under it. A form reads label, input, label, input (project-wide rule,
   * 14 Oct 2026); what a field is for or how to fill it is one hover away.
   */
  hint?: string;
  /** More background behind the same ⓘ. */
  info?: ReactNode;
  /**
   * Live feedback that depends on what was typed — "Looking up…", "3 post
   * offices · Pune", "Searching…" — shown under the label. The only text
   * besides an error that a field shows on its own.
   */
  status?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  /** Given the wiring so the control does not have to be told twice. */
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
    invalid: boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const statusId = status ? `${id}-status` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [statusId, errorId].filter(Boolean).join(' ') || undefined;
  const tip = hint && info ? <>{hint} {info}</> : (info ?? hint);

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* ⓘ beside the label, not inside it: a button inside a <label> would
          hand its click to the control. */}
      {/* As tall as an ⓘ either way, so a field with one lines up with its neighbour. */}
      <div className="flex min-h-5 items-center gap-1">
        <label htmlFor={id} className="block text-caption text-neutral-700">
          {label}
          {required && <span className="ml-0.5 text-danger-600" aria-hidden>*</span>}
          {required && <span className="sr-only">(required)</span>}
        </label>
        {tip ? <InfoTip label={`About ${label}`}>{tip}</InfoTip> : null}
      </div>
      {status && <p id={statusId} aria-live="polite" className="text-caption font-normal text-neutral-500">{status}</p>}
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        invalid: Boolean(error),
      })}
      {/* The message sits with the field, not in a banner at the top of a form
          the user has already scrolled past. */}
      {error && (
        <p id={errorId} role="alert" className="text-caption font-normal text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

/**
 * A date field: the browser's own date input, visible and styled like every
 * other control. It types (day, month, year in the browser's order) and
 * opens the browser's calendar; value and onChange are the ISO string
 * ("1985-03-15"). The owner asked for exactly this on 26 Oct 2026 after a
 * custom face over an invisible input proved hard to use in Firefox.
 */
function DateInput({ invalid, className, ...rest }: InputProps) {
  return (
    <input
      type="date"
      {...rest}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL, 'h-10',
        invalid ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400',
        rest.disabled && 'bg-neutral-50 text-neutral-500',
        className,
      )}
    />
  );
}

export function Input({ invalid, className, type, ...rest }: InputProps) {
  if (type === 'date') return <DateInput invalid={invalid} className={className} {...rest} />;
  // A checkbox or radio is a mark, not a field: given the field's `h-10
  // w-full` it grows into a block the size of the label beside it, which is
  // what every capability picker and decision card looked like.
  if (type === 'checkbox' || type === 'radio') {
    return (
      <input
        type={type}
        {...rest}
        className={cn(
          'h-4 w-4 shrink-0 cursor-pointer border-neutral-300 accent-primary-600',
          type === 'checkbox' ? 'rounded-badge' : 'rounded-full',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      />
    );
  }
  return (
    <input
      type={type}
      {...rest}
      className={cn(
        CONTROL, 'h-10',
        invalid ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400',
        className,
      )}
    />
  );
}

export function Textarea({ invalid, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      {...rest}
      className={cn(
        CONTROL, 'min-h-20 py-2 leading-5',
        invalid ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400',
        className,
      )}
    />
  );
}

export function Select({ invalid, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      {...rest}
      className={cn(
        CONTROL, 'h-10 appearance-none bg-no-repeat pr-9',
        invalid ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400',
        className,
      )}
      // The chevron is a background image rather than an overlaid element so
      // the control stays a single focusable, clickable target.
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23626262' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 10px center',
        ...rest.style,
      }}
    >
      {children}
    </select>
  );
}

/** A checkbox with its label, sized so the label is part of the hit target. */
export function Checkbox({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    // The label is the target: 24px tall at least, 40px on a touch screen,
    // so the 16px box is never the only thing a thumb can hit.
    <label className={cn('inline-flex min-h-6 cursor-pointer items-center gap-2 text-body text-neutral-700 pointer-coarse:min-h-10', className)}>
      <input
        type="checkbox"
        {...rest}
        className="h-4 w-4 rounded-badge border-neutral-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
      />
      {label}
    </label>
  );
}

/**
 * A search box.
 *
 * Separate from `Input` because it carries the magnifier, and because a search
 * that clears itself needs the clear affordance to be part of the control
 * rather than a button somebody remembers to add.
 */
export function SearchInput({
  value, onValueChange, placeholder = 'Search', className, ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  value: string;
  onValueChange: (v: string) => void;
}) {
  return (
    <div className={cn('relative', className)}>
      <svg
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
      </svg>
      <input
        {...rest}
        type="search"
        role="searchbox"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        // The control draws its own clear button; the browser's native one
        // beside it read as two ways to do the same thing.
        className={cn(CONTROL, 'h-10 border-neutral-300 pl-9 hover:border-neutral-400 [&::-webkit-search-cancel-button]:hidden',
          value && 'pr-9')}
      />
      {value && (
        <button
          type="button"
          onClick={() => onValueChange('')}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-neutral-500 max-md:right-0 max-md:h-10 max-md:w-10 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      )}
    </div>
  );
}

/**
 * A labelled text field — the common case, in one element.
 *
 * `Field` above is the general form and takes a render prop, which is right
 * when the control is a select, a group of radios or something bespoke. Most
 * of the product is a label and a text input, and asking every one of those to
 * spell out the render prop is how people end up writing a bare `<input>`
 * instead.
 */
export function TextField({
  label, value, onChange, hint, info, status, error, mono, required, autoFocus, placeholder,
  type = 'text', disabled, className, inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  /** Live feedback under the label — see `Field`. */
  status?: ReactNode;
  /** Background behind ⓘ beside the label — see `Field`. */
  info?: ReactNode;
  error?: string | null;
  /** For codes, GSTINs and registration numbers, which are read character by character. */
  mono?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  return (
    <Field label={label} hint={hint} info={info} status={status} error={error} required={required} className={className}>
      {(p) => (
        <Input
          {...p}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          required={required}
          // Codes and identifiers must not be silently "corrected" by the
          // browser: an autocorrected GSTIN is a wrong GSTIN.
          spellCheck={false}
          autoCapitalize={mono ? 'characters' : undefined}
          className={mono ? 'font-mono' : undefined}
        />
      )}
    </Field>
  );
}

/** The same, for a select. */
export function SelectField({
  label, value, onChange, hint, info, status, error, required, disabled, children, className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  /** Live feedback under the label — see `Field`. */
  status?: ReactNode;
  /** Background behind ⓘ beside the label — see `Field`. */
  info?: ReactNode;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Field label={label} hint={hint} info={info} status={status} error={error} required={required} className={className}>
      {(p) => (
        <Select {...p} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          {children}
        </Select>
      )}
    </Field>
  );
}

/**
 * A read-only label and value.
 *
 * The counterpart to a field, for the many screens that show a record before
 * offering to change it.
 */
export function Detail({ label, value, mono, className }: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-caption font-medium text-neutral-600">{label}</dt>
      <dd className={cn('mt-0.5 text-body text-neutral-900', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}
